// Coordination flag for drag-move operations.
// When a step is dragged, the target inserts the copy and navigates to its new
// ID, then the source removes the original. This flag is set only around that
// removal so onStepDelete does not navigate away from the new step.
let stepMoveInProgress = false;

export function setStepMoveInProgress(value) {
  stepMoveInProgress = value;
}

export function isStepMoveInProgress() {
  return stepMoveInProgress;
}

export function buildStepMaps(steps) {
  const stepMap = new Map();
  const jumpTargetMap = new Map();

  function walk(stepsArray) {
    for (let i = 0; i < stepsArray.length; i += 1) {
      const step = stepsArray[i];
      stepMap.set(step.id, step);

      if (step.action === 'jump_to_step') {
        const targetId = step.attributes?.step_id;
        if (targetId) {
          if (!jumpTargetMap.has(targetId)) jumpTargetMap.set(targetId, []);
          jumpTargetMap.get(targetId).push(step);
        }
      }

      if (step.default) {
        if (step.default.id) stepMap.set(step.default.id, step.default);
        if (step.default.steps) walk(step.default.steps);
      }

      if (step.branches) {
        for (let j = 0; j < step.branches.length; j += 1) {
          const branch = step.branches[j];
          if (branch.id) stepMap.set(branch.id, branch);
          if (branch.steps) walk(branch.steps);
        }
      }

      if (step.action === 'for_each' && step.steps) {
        walk(step.steps);
      }
    }
  }

  walk(steps);
  return { stepMap, jumpTargetMap };
}

export function getParentSteps(steps, stepId, parents = []) {
  const parentsCopy = Array.from(parents);
  for (let i = 0; i < steps.length; i += 1) {
    const step = steps[i];
    if (step.id === stepId) return parentsCopy;

    if (i || parents.length === 0) {
      parentsCopy.push(step);
    }

    if (step.action === 'for_each') {
      const forEachSteps = step.steps ?? [];
      // Start with empty parents — jump targets are scoped to the loop body
      const foundInForEach = getParentSteps(forEachSteps, stepId, []);
      if (foundInForEach) return foundInForEach;
    }

    if (step.action === 'branch') {
      const defaultSteps = step.default?.steps ?? [];
      const branches = step.branches ?? [];

      const firstDefaultStep = defaultSteps[0];
      const firstBranchesSteps = branches.flatMap(b => b.steps?.[0]);
      const firstSteps = [...firstBranchesSteps, firstDefaultStep]
        .filter(Boolean)
        .filter(({ id }) => id !== stepId);
      parentsCopy.push(...firstSteps);

      const found = getParentSteps(defaultSteps, stepId, parentsCopy);
      if (found) return found;

      for (let j = 0; j < branches.length; j += 1) {
        const branch = branches[j];
        const branchSteps = branch.steps ?? [];
        const foundInBranch = getParentSteps(branchSteps, stepId, parentsCopy);
        if (foundInBranch) return foundInBranch;
      }
    }
  }
  return null;
}
